import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, EnvelopeIcon, ListBulletIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { emailService } from '../services/emailService';
import { fetchPublicIp, formatDate, formatFileSize, startFileDownload } from '../lib/format';
import { Release } from '../types';
import DownloadsModal from './DownloadsModal';
import ConfirmDialog from './ConfirmDialog';
import EditReleaseModal from './EditReleaseModal';
import { notifyNewRelease } from './CreateReleaseModal';
import { Spinner } from './ui';

interface ReleaseCardProps {
  release: Release;
  projectName: string;
  canManage: boolean;
  isLatest: boolean;
  isLast: boolean;
  onChanged: () => void;
}

const NOTES_PREVIEW_LENGTH = 220;

/** One entry on the project's release timeline. */
const ReleaseCard: React.FC<ReleaseCardProps> = ({ release, projectName, canManage, isLatest, isLast, onChanged }) => {
  const { t, language } = useLanguage();
  const { user, appUser } = useAuth();
  const [showDownloads, setShowDownloads] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const longNotes = release.description.length > NOTES_PREVIEW_LENGTH;
  const notes =
    longNotes && !notesOpen ? `${release.description.slice(0, NOTES_PREVIEW_LENGTH).trimEnd()}…` : release.description;

  const sendDownloadNotice = async (downloadId: string) => {
    try {
      await emailService.sendDownloadNotification(downloadId);
    } catch (error) {
      // The download itself succeeded; the e-mail is a courtesy notice.
      console.warn('Download notification failed:', error);
    }
  };

  const handleDownload = async () => {
    if (!user) return;
    setDownloading(true);
    try {
      const ipAddress = await fetchPublicIp();
      const downloadId = await projectService.recordDownload(
        release.id,
        release.projectId,
        user.uid,
        user.email || '',
        appUser?.displayName || user.displayName || user.email || '',
        ipAddress
      );
      startFileDownload(release.fileUrl, release.fileName);
      toast.success(t('releases.downloadStarted'));
      sendDownloadNotice(downloadId);
    } catch (error) {
      console.error('Error recording download:', error);
      toast.error(t('releases.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await projectService.deleteRelease(release);
      toast.success(t('releases.deleted'));
      setShowDelete(false);
      onChanged();
    } catch (error) {
      console.error('Error deleting release:', error);
      toast.error(t('releases.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <li className="relative flex gap-4 sm:gap-6">
      {/* Timeline rail */}
      <div className="relative flex w-4 shrink-0 justify-center" aria-hidden="true">
        {!isLast && <span className="absolute bottom-0 top-6 w-px bg-gray-200 dark:bg-gray-800" />}
        <span
          className={`relative mt-[22px] h-3 w-3 rounded-full ${
            isLatest
              ? 'bg-signal-500 ring-4 ring-signal-100 dark:ring-signal-900/50'
              : 'border-2 border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-950'
          }`}
        />
      </div>

      <article className={`card mb-4 min-w-0 flex-1 p-4 sm:p-5 ${isLatest ? 'border-signal-200 dark:border-signal-900/70' : ''}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[15px] font-semibold text-gray-900 dark:text-white">v{release.version}</span>
              {isLatest && <span className="badge bg-signal-50 text-signal-700 dark:bg-signal-900/40 dark:text-signal-300">{t('releases.latest')}</span>}
            </div>
            <h3 className="mt-0.5 text-base font-medium text-gray-800 dark:text-gray-200">{release.title}</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {formatDate(release.uploadedAt, language, true)}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading || !release.fileUrl}
              className={`btn btn-sm ${isLatest ? 'btn-primary' : 'btn-secondary'}`}
            >
              {downloading ? <Spinner className="h-4 w-4" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
              {t('releases.download')}
            </button>
            <button
              type="button"
              onClick={() => setShowDownloads(true)}
              className="btn btn-ghost btn-icon"
              title={t('releases.downloadLog')}
              aria-label={t('releases.downloadLog')}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
            {canManage && (
              <>
                <button
                  type="button"
                  onClick={() => setShowResend(true)}
                  className="btn btn-ghost btn-icon"
                  title={t('releases.resend')}
                  aria-label={t('releases.resend')}
                >
                  <EnvelopeIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowEdit(true)}
                  className="btn btn-ghost btn-icon"
                  title={t('releases.edit')}
                  aria-label={t('releases.edit')}
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowDelete(true)}
                  className="btn btn-danger-ghost btn-icon"
                  title={t('releases.delete')}
                  aria-label={t('releases.delete')}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>

        {release.description && (
          <div className="mt-3 border-l-2 border-gray-200 pl-3 dark:border-gray-700">
            <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-gray-600 dark:text-gray-300">{notes}</p>
            {longNotes && (
              <button
                type="button"
                onClick={() => setNotesOpen((v) => !v)}
                className="mt-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                {notesOpen ? t('releases.showLess') : t('releases.showMore')}
              </button>
            )}
          </div>
        )}

        <p className="mt-3 flex min-w-0 items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="truncate font-mono">{release.fileName}</span>
          <span className="shrink-0">{formatFileSize(release.fileSize)}</span>
        </p>
      </article>

      {showDownloads && (
        <DownloadsModal
          release={release}
          projectName={projectName}
          canManage={canManage}
          onClose={() => setShowDownloads(false)}
        />
      )}

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title={t('releases.deleteTitle')}
        message={t('releases.deleteConfirm', { version: release.version, title: release.title })}
        confirmText={t('releases.delete')}
        loading={deleting}
      />

      <ConfirmDialog
        isOpen={showResend}
        onClose={() => setShowResend(false)}
        onConfirm={() => {
          setShowResend(false);
          notifyNewRelease(release.projectId, release.id, t);
        }}
        title={t('releases.resendTitle')}
        message={t('releases.resendConfirm', { version: release.version })}
        confirmText={t('releases.resendButton')}
        type="info"
      />

      {showEdit && (
        <EditReleaseModal
          release={release}
          onClose={() => setShowEdit(false)}
          onSuccess={(notify) => {
            setShowEdit(false);
            onChanged();
            if (notify) notifyNewRelease(release.projectId, release.id, t);
          }}
        />
      )}
    </li>
  );
};

export default ReleaseCard;
